import { QuickFilterOption } from "../QuickFilterOption";
import { ProcessFilterZod, ArchivedFilter, ClientTagWithProcessCount, GroupBy } from "@avenueops/shared-types";
import { ProcessFilterZodArray } from "@avenueops/shared-types/src";
import { TagIcon } from "components/tags/TagIcon";
import { TimeFrameArgs } from "context/ProcessTableContext";
import _ from "lodash";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { trpc } from "utils/trpc";

interface Props {
  timeFrameContext: TimeFrameArgs;
  filters?: ProcessFilterZodArray;
  isFilterActive: (filter: ProcessFilterZod) => boolean;
  setQuickFilter: (filter: ProcessFilterZod) => void;
  clearQuickFilter: () => void;
  archived: ArchivedFilter;
  isDashboard: boolean;
}

export default function TagQuickFilters({ timeFrameContext, filters, isFilterActive, setQuickFilter, clearQuickFilter, archived, isDashboard }: Props) {
  const { timeFrame, timeFrameStartIncl, timeFrameEndExcl } = timeFrameContext;

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { isLoading, data: tags } = trpc.tags.getOrgTagsAndTaggedProcessCounts.useQuery({
    timeFrame: timeFrame,
    timeFrameStartIncl: timeFrameStartIncl?.toISOString(),
    timeFrameEndExcl: timeFrameEndExcl?.toISOString(),
    filters: filters ?? [],
    timeZone,
    archived,
    isDashboard,
  });

  // Also fetch the process counts grouped by workflow to get the total count (since the workflow groups are mutually exclusive).
  const { data: processGroupCounts, isLoading: processGroupCountsLoading } = trpc.processes.countByGroup.useQuery({
    groupBy: GroupBy.WORKFLOW,
    timeFrame: timeFrame,
    timeFrameStartIncl: timeFrameStartIncl?.toISOString(),
    timeFrameEndExcl: timeFrameEndExcl?.toISOString(),
    filters: filters ?? [],
    timeZone,
    archived,
    isDashboard,
  });

  const totalCount = processGroupCounts?.reduce((total: number, group) => {
    return (group.count ?? 0) + total;
  }, 0);

  return (
    <>
      {isLoading || processGroupCountsLoading ? (
        <div className="mt-7 mb-5 w-full px-5 relative z-0">
          <Skeleton count={3} className="w-full mb-2 text-xl" />
        </div>
      ) : !tags || tags.length === 0 ? (
        <div className="my-5 text-sm text-gray-400 text-center w-full">No quick filters found</div>
      ) : (
        <div className="divide-y divide-gray-200 px-5">
          {tags?.map((tag: ClientTagWithProcessCount, index: number) => (
            <div key={index}>
              <QuickFilterOption
                title={tag.name}
                icon={<TagIcon tag={tag} />}
                active={isFilterActive({ type: "TAG", value: [tag.id] } as ProcessFilterZod)}
                enable={() => setQuickFilter({ type: "TAG", value: [tag.id] } as ProcessFilterZod)}
                disable={clearQuickFilter}
                count={tag.processCount ?? 0}
                clickable={true}
                totalCount={totalCount}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
